import React, { useState } from 'react';
import {
  ChevronUp,
  ChevronDown,
  User
} from 'lucide-react';
import YouTubeEstimateTooltip from '../YouTubeEstimateTooltip';
import {
  formatNumber,
  formatCurrency,
  formatPercent,
  formatWatchHours,
  getPlatformColor,
  getTierColor,
  getStatusColor
} from '../../utils/formatters';
import { getFollowerLabel } from '../../utils/platformHelpers';

const CreatorTable = ({ data, compact = false }) => {
  const [sortBy, setSortBy] = useState('views');
  const [sortOrder, setSortOrder] = useState('desc');

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortBy) {
      case 'name':
        aVal = a.creator.name.toLowerCase();
        bVal = b.creator.name.toLowerCase();
        return sortOrder === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      case 'views':
        aVal = a.metrics.views;
        bVal = b.metrics.views;
        break;
      case 'engagement':
        aVal = a.metrics.totalEngagement;
        bVal = b.metrics.totalEngagement;
        break;
      case 'engagementRate':
        aVal = a.metrics.engagementRate;
        bVal = b.metrics.engagementRate;
        break;
      case 'avgCcv':
        aVal = a.metrics.avgCcv;
        bVal = b.metrics.avgCcv;
        break;
      case 'roi':
        aVal = a.roi.viewsPerDollar;
        bVal = b.roi.viewsPerDollar;
        break;
      case 'spend':
        aVal = a.campaign.agreedRate || 0;
        bVal = b.campaign.agreedRate || 0;
        break;
      default:
        aVal = a.metrics.views;
        bVal = b.metrics.views;
    }
    
    return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' 
      ? <ChevronUp className="w-4 h-4 ml-1" /> 
      : <ChevronDown className="w-4 h-4 ml-1" />;
  };

  const HeaderCell = ({ field, label, className = '' }) => (
    <th 
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center">
        {label}
        <SortIcon field={field} />
      </div>
    </th>
  );

  if (compact) {
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <HeaderCell field="name" label="Creator" />
              <HeaderCell field="views" label="Views" className="text-right" />
              <HeaderCell field="engagement" label="Engagement" className="text-right" />
              <HeaderCell field="engagementRate" label={<span className="flex items-center gap-1">Rate <YouTubeEstimateTooltip metric="engagement" /></span>} className="text-right" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedData.map((item) => (
              <tr key={item.creator.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center">
                    {item.creator.profileImageUrl ? (
                      <img 
                        src={item.creator.profileImageUrl} 
                        alt={item.creator.name}
                        className="w-8 h-8 rounded-full mr-3"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                        <User className="w-4 h-4 text-gray-500" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{item.creator.name}</p>
                      <p className="text-xs text-gray-500">
                        <span 
                          className="inline-block w-2 h-2 rounded-full mr-1"
                          style={{ backgroundColor: getPlatformColor(item.creator.platform) }}
                        />
                        @{item.creator.handle}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                  {formatNumber(item.metrics.views)}
                </td>
                <td className="px-4 py-3 text-right text-sm text-gray-600">
                  {formatNumber(item.metrics.totalEngagement)}
                </td>
                <td className="px-4 py-3 text-right text-sm text-gray-600">
                  {formatPercent(item.metrics.engagementRate)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="card-pink-accent bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <HeaderCell field="name" label="Creator" />
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Platform
              </th>
              <HeaderCell field="views" label="Views" className="text-right" />
              <HeaderCell field="engagement" label="Engagement" className="text-right" />
              <HeaderCell field="engagementRate" label={<span className="flex items-center gap-1">Eng. Rate <YouTubeEstimateTooltip metric="engagement" /></span>} className="text-right" />
              <HeaderCell field="avgCcv" label={<span className="flex items-center gap-1">Avg CCV <YouTubeEstimateTooltip metric="watchHours" /></span>} className="text-right" />
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Peak CCV
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center justify-end gap-1">
                  Watch Hours
                  <YouTubeEstimateTooltip metric="watchHours" />
                </div>
              </th>
              <HeaderCell field="spend" label="Spend" className="text-right" />
              <HeaderCell field="roi" label="Views/$" className="text-right" />
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedData.map((item) => (
              <tr key={item.creator.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-4">
                  <div className="flex items-center">
                    {item.creator.profileImageUrl ? (
                      <img 
                        src={item.creator.profileImageUrl} 
                        alt={item.creator.name}
                        className="w-10 h-10 rounded-full mr-3"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                        <User className="w-5 h-5 text-gray-500" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{item.creator.name}</p>
                      <p className="text-sm text-gray-500">@{item.creator.handle}</p>
                      {item.creator.tier && (
                        <span 
                          className="inline-block px-2 py-0.5 text-xs rounded-full mt-1"
                          style={{ 
                            backgroundColor: getTierColor(item.creator.tier) + '20',
                            color: getTierColor(item.creator.tier)
                          }}
                        >
                          {item.creator.tier}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span 
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                    style={{ 
                      backgroundColor: getPlatformColor(item.creator.platform) + '20',
                      color: getPlatformColor(item.creator.platform)
                    }}
                  >
                    {({ youtube: 'YouTube', twitch: 'Twitch', kick: 'Kick', tiktok: 'TikTok', instagram: 'Instagram', twitter: 'Twitter' }[item.creator.platform?.toLowerCase()] || item.creator.platform)}
                  </span>
                  {item.creator.followerCount && (
                    <p className="text-xs text-gray-500 mt-1">
                      {formatNumber(item.creator.followerCount)} {getFollowerLabel(item.creator.platform).toLowerCase()}
                    </p>
                  )}
                </td>
                <td className="px-4 py-4 text-right">
                  <p className="font-medium text-gray-900">{formatNumber(item.metrics.views)}</p>
                  {item.metrics.vodViews > 0 && (
                    <p className="text-xs text-gray-500">+{formatNumber(item.metrics.vodViews)} VOD</p>
                  )}
                </td>
                <td className="px-4 py-4 text-right">
                  <p className="font-medium text-gray-900">{formatNumber(item.metrics.totalEngagement)}</p>
                  <p className="text-xs text-gray-500">
                    {formatNumber(item.metrics.likes)} / {formatNumber(item.metrics.comments)}
                  </p>
                </td>
                <td className="px-4 py-4 text-right">
                  <p className={`font-medium ${item.metrics.engagementRate > 3 ? 'text-green-600' : 'text-gray-900'}`}>
                    {formatPercent(item.metrics.engagementRate)}
                  </p>
                </td>
                <td className="px-4 py-4 text-right font-medium text-gray-900">
                  {item.metrics.avgCcv > 0 ? formatNumber(item.metrics.avgCcv) : '-'}
                </td>
                <td className="px-4 py-4 text-right text-gray-600">
                  {item.metrics.peakCcv > 0 ? formatNumber(item.metrics.peakCcv) : '-'}
                </td>
                <td className="px-4 py-4 text-right text-gray-600">
                  {item.metrics.watchHours > 0 ? formatWatchHours(item.metrics.watchHours) : '-'}
                </td>
                <td className="px-4 py-4 text-right font-medium text-gray-900">
                  {formatCurrency(item.campaign.agreedRate || 0)}
                </td>
                <td className="px-4 py-4 text-right">
                  <p className={`font-medium ${item.roi.viewsPerDollar > 100 ? 'text-green-600' : 'text-gray-900'}`}>
                    {formatNumber(item.roi.viewsPerDollar)}
                  </p>
                </td>
                <td className="px-4 py-4 text-center">
                  <span 
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                    style={{ 
                      backgroundColor: getStatusColor(item.campaign.status) + '20',
                      color: getStatusColor(item.campaign.status)
                    }}
                  >
                    {item.campaign.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Summary row */}
      <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">
            {data.length} creators
          </span>
          <div className="flex items-center space-x-6 text-gray-600">
            <span>
              Total Views: <strong className="text-gray-900">
                {formatNumber(data.reduce((sum, c) => sum + c.metrics.views, 0))}
              </strong>
            </span>
            <span>
              Total Spend: <strong className="text-gray-900">
                {formatCurrency(data.reduce((sum, c) => sum + (c.campaign.agreedRate || 0), 0))}
              </strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatorTable;
